import { Camera } from '@capacitor/camera';
import { captureException } from '@fabric-app/compat/capabilities/sentry';
import { fileTypeFromBuffer } from 'file-type';
import pLimit from 'p-limit';
import { CameraError } from '../mediaRecording.types.js';
import { stripExtension, getFileNameFromPath, base64ToUint8Array } from '@fabric-app/utils/files';
import { isError, ensureError } from '@fabric-app/utils/guards';
import { toast } from '../../store/alerts.js';
import { getCompatFile } from '@fabric-app/compat/capabilities/file';

const pickImages = async (options) => {
  try {
    const fileCompat = getCompatFile();
    if (!fileCompat.getIsAvailable()) {
      return new Promise((resolve, reject) => {
        const input = document.createElement("input");
        input.type = "file";
        input.multiple = options.limit ? options.limit > 1 : true;
        input.accept = "image/*";
        input.onchange = async (e) => {
          const files = Array.from(e.target.files || []);
          if (files.length === 0) {
            reject(new Error(CameraError.Cancel));
            return;
          }
          files.forEach((file) => options.onImageProcessed?.(file));
          resolve(files);
        };
        input.oncancel = () => {
          reject(new Error(CameraError.Cancel));
        };
        input.click();
      });
    }
    const result = await Camera.pickImages({
      limit: options.limit
    });
    const photos = result.photos;
    if (!photos || photos.length === 0) throw new Error(CameraError.Cancel);
    const limit = pLimit(5);
    const promises = photos.map((photo) => limit(async () => {
      const fileCompat2 = getCompatFile();
      const path = photo.path ?? photo.webPath;
      if (!path) throw new Error("No image path");
      const { content } = await fileCompat2.readFile({
        url: path
      });
      let name = stripExtension(getFileNameFromPath(path));
      let contentType = "image/jpeg";
      const binary = base64ToUint8Array(content, 4100);
      const parsedFileType = await fileTypeFromBuffer(binary);
      if (parsedFileType) {
        name += `.${parsedFileType.ext}`;
        contentType = parsedFileType.mime;
      }
      const byteArray = Buffer.from(content, "base64");
      const blob = new Blob([
        byteArray
      ], {
        type: contentType
      });
      const file = new File([
        blob
      ], name, {
        type: contentType
      });
      options.onImageProcessed?.(file);
      return file;
    }));
    options.onStartProcessing?.();
    return (await Promise.allSettled(promises)).filter((promise) => promise.status === "fulfilled").map((promise) => promise.value);
  } catch (error) {
    if (isError(error) && error.message === CameraError.Cancel) {
      throw ensureError(error);
    }
    if (isError(error) && (error.name === "NotAllowedError" || error.name === "PermissionDeniedError" || error.name === "SecurityError" || error.message === CameraError.PermissionCameraDenied || error.message === CameraError.PermissionPhotosDenied)) {
      toast({
        id: "camera-permission-denied",
        content: "You need to grant access to your photos",
        replace: true
      });
      throw ensureError(error);
    }
    captureException(ensureError(error));
    throw ensureError(error);
  }
};

export { pickImages };
//# sourceMappingURL=pickImages.js.map
